// Typed message endpoint functions. Backed by GET /messages canonical
// endpoints from src/api/app/routers/messages.py + the v4
// messaging-platform module per CHG-1237 module map.
//
// IMPORTANT (CHG-1237 intent-vs-delivery distinction): the message entity
// tracks ORCHESTRATION INTENTS — draft / scheduled / dispatched. Per-
// recipient delivery rows (sent_at, opened_at, clicked_at, bounced) live
// in the canonical `message_deliveries` table (`MessageDelivery` ORM).
// CONV-C5.1.A (CHG-2088..CHG-2104) consolidated the outbox: the legacy
// `delivery_logs` table + ORM were retired in CHG-2103. The Recipients
// tab on the detail page bridges to the canonical
// GET /messages/{id}/deliveries endpoint via getMessageDeliveries(),
// which returns [] today pending the v2 wire-through.

import { apiClient } from "./client";
import { getActiveOrgId } from "../contexts/OrgContext";
import {
  normalizeListResponse,
  type NormalizedList,
  type RawListEnvelope,
} from "./list-contract";
import type { PagedEnvelope } from "./types";

// CHG-1648 / ITER-3757 — canonical BE path is
// /api/v1/organizations/{org_id}/messages (messages.py router prefix).
// The v2 client previously called bare "/messages" which 404s — there's
// no flat /messages router. We read the active org from localStorage
// (OrgContext.ACTIVE_ORG_STORAGE_KEY) since the screen-config queryFn
// closures can't use the useOrg() hook. When no active org is set, fall
// back to "current" so the BE 422s with a clear "Invalid org_id" instead
// of a misleading 404.
function messagesBase(): string {
  const orgId = getActiveOrgId() ?? "current";
  return `/organizations/${orgId}/messages`;
}

export type MessageStatus =
  | "draft"
  | "scheduled"
  | "sending"
  | "sent"
  | "partial"
  | "failed"
  | "expired"
  | "cancelled";

export type MessageKind =
  | "live_session"
  | "quick"
  | "standard"
  | "campaign_arc"
  | "seasonal"
  | "social_post";

export type MessageTriggerKind =
  | "immediate"
  | "scheduled"
  | "recurring"
  | "event";

export interface Message {
  id: string;
  organization_id: string;
  intent_title: string;
  intent_body: string | null;
  intent_tone: string | null;
  kind: MessageKind;
  status: MessageStatus;
  // channel_plan is JSONB in the canonical T2 entity (a list of per-
  // channel sub-plans). Both GET /messages (list) and GET /messages/{id}
  // (detail) return a pre-stringified summary (e.g., "email + sms" /
  // "push") for list-page projection — the stringification logic lives
  // in src/api/app/routers/messages.py and is consistent across both
  // endpoints. If a future API revision tightens this to a typed object,
  // narrow the v2 type here (and update the channel_plan list column
  // type from "text" to a custom renderer).
  channel_plan: string | null;
  trigger_kind: MessageTriggerKind;
  scheduled_at: string | null;
  arc_id: string | null;
  campaign_id: string | null;
  designation_id: string | null;
  recurring_rule: string | null;
  event_type: string | null;
  created_at: string;
  updated_at: string;
}

export type MessagePagedEnvelope = PagedEnvelope<Message>;

// Per-recipient delivery row projected from the canonical `message_deliveries`
// table (`MessageDelivery` ORM; T2 entity at
// specs/tier-02-domain/message-delivery.entity.yaml). Shape is conservative
// vs the full MessageDeliveryRead — narrow to what the Recipients tab
// surfaces. CONV-C5.1.A consolidated this outbox; legacy `delivery_logs`
// ORM retired CHG-2103 / CONV-C5.1.A.5.
export interface MessageDeliveryEntry {
  id: string;
  recipient: string;
  channel: "email" | "sms" | "push" | "social";
  status: "queued" | "sent" | "delivered" | "opened" | "clicked" | "bounced" | "complained";
  sent_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  bounced_at: string | null;
}

export interface MessageAuditEntry {
  id: string;
  kind: string;
  actor: string;
  occurred_at: string;
  description: string;
}

export interface MessageRelatedDonationEntry {
  id: string;
  donor_name: string;
  amount_minor_units: number;
  currency: string;
  attributed_at: string;
}

export interface ListMessagesParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  status?: MessageStatus;
  kind?: MessageKind;
}

export async function listMessages(
  params: ListMessagesParams = {},
): Promise<NormalizedList<Message>> {
  // CHG-2916 / ITER-905655 — the org-scoped messages router
  // (src/api/app/routers/messages.py:list_messages) returns the
  // `{ items, total, cursor }` MessageList envelope and reads 1-based
  // `page` / `per_page` plus `status` / `kind` (+ campaign_id /
  // designation_id). It has NO `sort` and NO `search`/`q` param — both
  // were dropped no-ops and are no longer sent. (The envelope here was
  // already `{ items, total }`, so only the request params were wrong.)
  const pageSize = params.pageSize ?? 50;
  const r = await apiClient().get<RawListEnvelope<Message>>(messagesBase(), {
    params: {
      page: (params.page ?? 0) + 1,
      per_page: pageSize,
      status: params.status,
      kind: params.kind,
    },
  });
  return normalizeListResponse<Message>(r.data);
}

export async function getMessage(id: string): Promise<Message> {
  const r = await apiClient().get<Message>(`${messagesBase()}/${id}`);
  return r.data;
}

// Forward-promised: per-recipient delivery rows from the canonical
// `message_deliveries` table (`MessageDelivery` ORM; CONV-C5.1.A
// consolidated the outbox — see CHG-2088..CHG-2103). Surfaced by the
// Recipients tab on MessageDetail. Returns [] until the v2 client
// wires through to GET /messages/{id}/deliveries.
export async function getMessageDeliveries(
  id: string,
): Promise<MessageDeliveryEntry[]> {
  void id;
  return [];
}

// Forward-promised: GET /messages/{id}/audit. Surfaced by the Audit tab.
export async function getMessageAudit(
  id: string,
): Promise<MessageAuditEntry[]> {
  void id;
  return [];
}

// Forward-promised: GET /messages/{id}/related-donations. Surfaced by
// the Related Donations tab.
export async function getMessageRelatedDonations(
  id: string,
): Promise<MessageRelatedDonationEntry[]> {
  void id;
  return [];
}

export const messagesApi = {
  listMessages,
  getMessage,
  getMessageDeliveries,
  getMessageAudit,
  getMessageRelatedDonations,
};
